import { lazy, type LazyExoticComponent, type ComponentType } from "react";
import {
  type JoinLayoutEditorProps,
  type OnyxConfig,
  ONYX_DEFAULTS,
  onyxConfigSchema,
  APPARITION_DEFAULTS,
  apparitionConfigSchema,
  AETHERIA_DEFAULTS,
  aetheriaConfigSchema,
  type z,
} from "@wedding/shared";

export interface OrganizerLayoutOption {
  id: string;
  label: string;
  tier: "original" | "premium";
  /** Static defaults for layouts that have a per-layout editor. */
  defaults?: unknown;
  /** Zod schema (used to validate stored config before saving). */
  schema?: z.ZodType<unknown>;
  /** Lazy-loaded editor component. */
  editor?: LazyExoticComponent<ComponentType<JoinLayoutEditorProps<unknown>>>;
}

const OnyxEditor = lazy(() => import("./onyx-editor"));
const ApparitionEditor = lazy(() => import("./apparition-editor"));
const AetheriaEditor = lazy(() => import("./aetheria-editor"));

// Mirror of apps/web layout registry, but only the layouts the organizer can
// configure get a `defaults` / `schema` / `editor`. The rest are pickable but
// fall back to read-only presets for now.
export const ORGANIZER_LAYOUTS: OrganizerLayoutOption[] = [
  // Synthetic "default" entry — uses the legacy preset/palette/fontPair UI.
  // NOTE: `label` is only a fallback — display names come from i18n
  // (themes.layoutNames.<id>); premium entries are brand names (no translation).
  { id: "default", label: "Classic (default)", tier: "original" },

  // ── Original 5 ──
  { id: "cinematic", label: "Cinematic", tier: "original" },
  { id: "minimal", label: "Minimal", tier: "original" },
  { id: "parallax", label: "Romantic Parallax", tier: "original" },
  { id: "editorial", label: "Bold Editorial", tier: "original" },
  { id: "dark-romance", label: "Dark Romance", tier: "original" },

  // ── Premium 13 ──
  { id: "atelier", label: "Atelier", tier: "premium" },
  { id: "velvet", label: "Velvet", tier: "premium" },
  {
    id: "onyx",
    label: "Onyx",
    tier: "premium",
    defaults: ONYX_DEFAULTS as unknown,
    schema: onyxConfigSchema as z.ZodType<unknown>,
    editor: OnyxEditor as LazyExoticComponent<ComponentType<JoinLayoutEditorProps<unknown>>>,
  },
  { id: "prism", label: "Prism", tier: "premium" },
  { id: "veil", label: "Veil", tier: "premium" },
  { id: "eclipse", label: "Eclipse", tier: "premium" },
  { id: "mirage", label: "Mirage", tier: "premium" },
  { id: "aurum", label: "Aurum", tier: "premium" },
  { id: "sonata", label: "Sonata", tier: "premium" },
  { id: "nebula", label: "Nebula", tier: "premium" },
  { id: "tide", label: "Tide", tier: "premium" },
  { id: "vedette", label: "Vedette", tier: "premium" },
  { id: "strata", label: "Strata", tier: "premium" },
  {
    id: "apparition",
    label: "Apparition",
    tier: "premium",
    defaults: APPARITION_DEFAULTS as unknown,
    schema: apparitionConfigSchema as z.ZodType<unknown>,
    editor: ApparitionEditor as LazyExoticComponent<ComponentType<JoinLayoutEditorProps<unknown>>>,
  },
  {
    id: "aetheria",
    label: "Aetheria",
    tier: "premium",
    defaults: AETHERIA_DEFAULTS as unknown,
    schema: aetheriaConfigSchema as z.ZodType<unknown>,
    editor: AetheriaEditor as LazyExoticComponent<ComponentType<JoinLayoutEditorProps<unknown>>>,
  },
];

export function getOrganizerLayout(id: string | undefined): OrganizerLayoutOption | undefined {
  if (!id) return undefined;
  return ORGANIZER_LAYOUTS.find((l) => l.id === id);
}

export type { OnyxConfig };
